import { AfterViewInit, Component, ErrorHandler, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, computed, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { email, form, required, validate } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'
import { Router } from '@angular/router'
import { arvonMuutokset } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'

import { KayttajaService } from '../_angular/service/kayttaja.service'
import { LoginInputData, LoginService } from '../_angular/service/login.service'
import { VersionTarkistusPalvelu } from '../_angular/service/version-tarkistus.palvelu'

import { LoginRequest, LoginResponse } from '@lemon/core-firebase/model/login'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'

import { VaihdaKieliDialog, VaihdaKieliDialogData } from '../vaihda-kieli.dialog'

import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { EnvironmentType } from 'app/app.environment'
import { environment } from 'environments/environment'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface KirjautumisLomake {
  email: string
  password: string
}

@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatInput, { static: true }) emailInput: MatInput

  private readonly _palvelimenVirheet = signal<Partial<Record<keyof KirjautumisLomake, string>>>({})

  lomake = form(signal<KirjautumisLomake>({ email: '', password: '' }), polku => {
    required(polku.email)
    email(polku.email)
    validate(polku.email, () => this._palvelimenVirhe('email'))
    required(polku.password)
    validate(polku.password, () => this._palvelimenVirhe('password'))
  })
  private readonly _kieli = toSignal(this._translationService.currentLanguageObservable)
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this._validationService.updateValidationStatus('login', this.lomake)
  })
  readonly commonError = signal<string | null>(null)
  readonly betaOffLimitsError = signal<string | null>(null)
  online = true
  year = new Date().getFullYear()
  inputToPassOn: LoginInputData

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _loginInflight: boolean = false

  constructor(
    private _kayttajaService: KayttajaService,
    private _router: Router,
    private _dialog: MatDialog,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _versionTarkistaja: VersionTarkistusPalvelu,
    private _loginService: LoginService,
    private _versionTarkistusPalvelu: VersionTarkistusPalvelu,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {
    this._loginService.clearCredentials()
    // Preheat
    this._firebaseLemonaid.functionsCall<string, void>('loginSalasanallaTallennaOngoing', '')
    // Palvelimen virhe on voimassa, kunnes kentän arvo muuttuu, kuten reaktiivisen lomakkeen setErrors
    for (const kentta of ['email', 'password'] as const) {
      arvonMuutokset(this.lomake[kentta]).subscribe(() => this._palvelimenVirheet.update(virheet => ({ ...virheet, [kentta]: undefined })))
    }
  }

  ngOnInit(): void {
    this._loginService.currentLoginCredentialsObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(data => (this.inputToPassOn = data))

    this._versionTarkistaja.sovelluksenVersioObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(versiotiedot => {
      this._versionTarkistaja.tarkistaVersio(versiotiedot)
    })

    this._kayttajaService.kayttajanTiedotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kayttajanTiedot => {
      if (kayttajanTiedot) {
        this.setLoginProcessEnded()
        this._router.navigate(['/'])
      }
    })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.emailInput.focus()
    }, 0)
  }

  setLoginProcessStarted() {
    this._ladataanService.aloitaLataaminen()
  }

  setLoginProcessEnded() {
    this._ladataanService.lopetaLataaminen()
    this._loginInflight = false
  }

  forgot() {
    this._router.navigate(['/unohdin'])
  }

  @HostListener('document:keydown.enter')
  login() {
    if (this._loginInflight) {
      return
    }
    this._loginInflight = true

    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      this._loginInflight = false
      return
    }

    this.setLoginProcessStarted()
    const payload: LoginRequest = {
      email: this.lomake.email().value(),
      password: this.lomake.password().value(),
      valittuKieli: this._translationService.nykyinenKieli,
      env: 'w',
      ver: this._versionTarkistusPalvelu.annaVersio(),
      device: navigator.platform,
      os: navigator.userAgent,
    }

    if (environment.environment === EnvironmentType.BETA) {
      payload.beta = true
    }

    this._firebaseLemonaid
      .functionsCall<LoginRequest, LoginResponse>('loginSalasanallaTallennaOngoing', payload)
      .then(data => {
        if (!data) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
          this._errorHandler.handleError('no-response-or-no-data')
          this.setLoginProcessEnded()
          return
        }

        const error = data.e

        const loginDataTransfer: LoginInputData = {
          email: this.lomake.email().value(),
          password: this.lomake.password().value(),
          loginCodeId: data.loginCodeId,
        }

        if (error) {
          this.setLoginProcessEnded()
          switch (error) {
            case 'auth/no-phone-number':
              this._loginService.changeInput(loginDataTransfer)
              this._router.navigate(['/lisaa-puhelin'])
              break
            case 'auth/user-not-found':
              this._asetaPalvelimenVirhe('email', 'user-not-found')
              break
            case 'auth/invalid-email':
              this._asetaPalvelimenVirhe('email', 'invalid-email')
              break
            case 'auth/user-disabled':
              this._asetaPalvelimenVirhe('email', 'user-disabled')
              break
            case 'auth/user-inactive':
              this._asetaPalvelimenVirhe('email', 'user-disabled')
              break
            case 'auth/wrong-password':
              this._asetaPalvelimenVirhe('password', 'wrong-password')
              break
            case 'auth/auth-failed':
              this._asetaPalvelimenVirhe('password', 'auth-failed')
              break
            case 'beta-off-limits':
              this.betaOffLimitsError.set(this._translationService.lokalisoi('login.beta-off-limits'))
              this._errorHandler.handleError(error)
              break
            default:
              this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
              this._errorHandler.handleError(error)
          }
          this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
        } else {
          this.setLoginProcessEnded()
          this._loginService.changeInput(loginDataTransfer)
          this._router.navigate(['/kirjaudu-vahvista'])
        }
      })
      .catch(error => {
        this.setLoginProcessEnded()
        this.commonError.set(typeof error === 'string' ? error : this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
        this._errorHandler.handleError(error)
      })
  }

  private _asetaPalvelimenVirhe(kentta: keyof KirjautumisLomake, laji: string) {
    this._palvelimenVirheet.update(virheet => ({ ...virheet, [kentta]: laji }))
  }

  private _palvelimenVirhe(kentta: keyof KirjautumisLomake) {
    const laji = this._palvelimenVirheet()[kentta]
    return laji ? { kind: laji } : null
  }

  vaihdaKieli() {
    const data: VaihdaKieliDialogData = {
      tallenna: false,
    }
    this._dialog.open(VaihdaKieliDialog, { data: data })
  }
}
